import { useEffect, useState } from 'react';
import { useAuctionStore } from '../stores/auction';
import AuctionCard from '../components/AuctionCard';
import Icon from '../components/Icon';
import { AUCTION_CATEGORIES, type AuctionCategory } from '@auction/shared';

const statuses = [
  { key: '', label: 'All', icon: 'apps' },
  { key: 'active', label: 'Active', icon: 'play_circle' },
  { key: 'ending', label: 'Ending Soon', icon: 'timer' },
  { key: 'closed', label: 'Closed', icon: 'lock' },
  { key: 'sold', label: 'Sold', icon: 'verified' },
];

// Category icon mapping for Material Icons
const categoryIcons: Record<string, string> = {
  electronics: 'devices',
  fashion: 'checkroom',
  collectibles: 'collections',
  vehicles: 'directions_car',
  home: 'home',
  sports: 'sports_soccer',
  toys: 'toys',
  art: 'palette',
  jewelry: 'diamond',
  other: 'category',
};

export default function HomePage() {
  const { auctions, loading, error, fetchAuctions, selectedCategory, setSelectedCategory } = useAuctionStore();
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchAuctions(filter || undefined, selectedCategory);
  }, [filter, selectedCategory, fetchAuctions]);

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Icon name="gavel" className="text-primary-600" />
            Live Auctions
          </h1>
          <div className="flex space-x-2">
            {statuses.map((s) => (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                  filter === s.key
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon name={s.icon} size="small" />
                {s.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Category filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
              selectedCategory === null
                ? 'bg-secondary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Icon name="category" size="small" />
            All Categories
          </button>
          {AUCTION_CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value as AuctionCategory)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
                selectedCategory === cat.value
                  ? 'bg-secondary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon name={categoryIcons[cat.value] || 'category'} size="small" />
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {!loading && auctions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No auctions found.</p>
          <p className="text-gray-400 mt-2">Try the Demo Panel to create some sample auctions!</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {auctions.map((auction) => (
          <AuctionCard key={auction.id} auction={auction} />
        ))}
      </div>
    </div>
  );
}
